import React from "react";
import { api } from "./api.jsx";
import { useApp } from "./store.jsx";
import { Dialog } from "./chrome.jsx";

const { Button, Input } = window.CardboardmarketDesignSystem_2a3239;

/**
 * "The set I am holding is not on your list."
 *
 * A DIALOG, ASKED FOR: "I believe that for 'my set is not on the list' it would
 * be best to use a dialog like we do for reporting images." So this is
 * `PictureReport`'s shape deliberately — quiet underlined trigger, a dialog that
 * explains what the report is for, a line for the one thing only they know, and
 * a thank-you that replaces the trigger rather than sitting beside it.
 *
 * The two reports are the same act from the seller's side: the catalogue is
 * wrong about the card in their hand, and they are the only person in a position
 * to say so. Making them look and behave alike is not tidiness — it is so that
 * the second one needs no learning once you have met the first.
 *
 * WHY IT IS A REPORT AND NOT A TEXT BOX ON THE LISTING. That is what it replaced:
 * "I prefer everything modeled than having a bunch of free text." A typed set
 * went into the listing, where no filter matches it and no import reconciles it,
 * and the gap it described stayed invisible to us — it looked like a set to
 * everything downstream and like nothing to the people who could add it. This
 * lands in a queue instead, and the back office groups it by set: once somebody
 * acts on it, that set is a chip like any other for every seller after.
 *
 * THE LISTING KEEPS THE SET THEY PICKED. Reporting a gap is not choosing a set,
 * and the two must not be confused — which is why the dialog says so out loud.
 */
export function SetReport({ productId, game }) {
  const { t } = useApp();
  const [open, setOpen] = React.useState(false);
  const [text, setText] = React.useState("");
  const [state, setState] = React.useState("idle");

  /*
   * A different card is a different report. Without this, somebody who reports a
   * gap and then changes the card sees "thank you" about a card they have left,
   * and the box would still hold what they typed for the last one.
   */
  React.useEffect(() => { setOpen(false); setText(""); setState("idle"); }, [productId]);

  /*
   * NO PRODUCT IS A CARD WE DO NOT HAVE AT ALL, and since 2026-10-12 that is a
   * case this report exists for: a title the catalogue does not know no longer
   * posts (every listing points at a real printing), so the way out for a promo
   * or a new release is to tell us, not to type it. This used to return null
   * here — "a card we have never heard of is answered by the text box" — and
   * typing no longer answers that case. The API takes a report with no
   * product: the column is nullable and the back office LEFT JOINs it, grouping
   * on the set name. The words change with it, because there is no list of
   * sets to be missing from and no listing keeping a set.
   */
  const known = Boolean(productId);

  const send = async () => {
    setState("sending");
    try {
      await api.reportMissingSet(text.trim(), productId, game);
      setState("sent");
      setOpen(false);
    } catch (e) {
      /* A report that did not send must not read as one that did. */
      setState("failed");
    }
  };

  if (state === "sent") {
    return (
      <p role="status" style={{ font: 'var(--type-meta)', color: 'var(--text-faint)', margin: 0 }}>
        {t("Thank you — we will add that set.")}
      </p>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        /* The same quiet treatment the picture report wears, and for the same
           reason: it sits under a control that is usually right, and it has to be
           findable by somebody already frowning at it without casting doubt on
           the times it is correct. */
        style={{
          font: 'var(--type-meta)', color: 'var(--text-faint)', background: 'none',
          border: 0, padding: 0, cursor: 'pointer', textDecoration: 'underline',
          textUnderlineOffset: 3, alignSelf: 'flex-start',
        }}
      >
        {known ? t("My set is not on this list") : t("My card is not on the list")}
      </button>
      {open ? (
        <Dialog
          open
          width={440}
          closeLabel={t("Close")}
          onClose={() => setOpen(false)}
          title={t("A set we do not have")}
          description={known
            ? t("Tell us what is printed on the card and we will add it to the catalogue. Your listing keeps the set you picked.")
            : t("Tell us what is printed on the card and we will add it to the catalogue.")}
          footer={<>
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={state === 'sending'}>
              {t("Cancel")}
            </Button>
            <Button onClick={send} disabled={!text.trim() || state === 'sending'}>
              {state === 'sending' ? t("Sending…") : t("Send")}
            </Button>
          </>}
        >
          <Input
            label={t("Which set is it?")}
            placeholder={t("As it is printed on the card")}
            value={text}
            onChange={(e) => setText(e.target.value)}
            hint={t("One line is enough. A set name, an edition, whatever the card says.")} />
          {state === 'failed' ? (
            <p role="alert" style={{ font: 'var(--type-caption)', color: 'var(--color-danger)', margin: 'var(--space-3) 0 0' }}>
              {t("That did not send. Try again in a moment.")}
            </p>
          ) : null}
        </Dialog>
      ) : null}
    </>
  );
}
